import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../Context/LanguageContext';
import PageTransition from './PageTransition';

// Sample announcements data
const announcements = [
  {
    id: 3,
    date: '2026-04-15',
    titleTr: 'Kıbrıs\'a Geri Dönüş Anketi Sonuçları Yayımlandı',
    titleEn: 'Return to Cyprus Survey Results Published',
    summaryTr: 'Yurt dışında yaşayan 145 genç profesyonelin katıldığı kapsamlı anketimizin sonuçları, engeller, beklentiler ve politika önerileriyle birlikte yayında.',
    summaryEn: 'The results of our comprehensive survey with 145 young professionals living abroad — covering barriers, expectations and policy proposals — are now published.',
    contentTr: `Kıbrıslı Türk Genç Profesyoneller Birliği (KTGPB) olarak, Ocak–Şubat 2026 tarihleri arasında yurt dışında yaşayan 145 genç profesyonelin katılımıyla gerçekleştirdiğimiz "Kıbrıs'a Geri Dönüş: Engeller ve İhtiyaçlar" anketinin sonuçlarını kamuoyu ile paylaşıyoruz.

Yaklaşık 30 sorudan oluşan bu kapsamlı çalışmada; kariyer engelleri, yaşam koşulları, altyapı ihtiyaçları, bilgi erişimi ve çalışma modeli tercihleri gibi konularda genç profesyonellerin deneyimlerini, kaygılarını ve beklentilerini doğrudan kendi perspektiflerinden değerlendirdik.

**📌 Öne çıkan bulgular:**
▪️ Katılımcıların %42'si dönüş konusunda koşullu veya kararsız. Doğru politikalarla bu kesim geri kazanılabilir
▪️ Liyakata dayalı kariyer ilerlemesi ve ücret tatmin ediciliği, kariyer faktörlerinin başında yer alıyor
▪️ Katılımcıların %81'i kendi alanında nitelikli pozisyon bulmayı zor veya çok zor buluyor
▪️ Elektrik sürekliliği (4.64/5) ve internet stabilitesi (4.45/5) tüm anketin en yüksek puanlı iki sorunu
▪️ Teşvik ve desteklere bilgi erişimi 5 üzerinden yalnızca 1.66. Katılımcıların %86'sı bilgiyi aile ve arkadaş çevresinden alıyor
▪️ Tercih edilen çalışma modeli yerel istihdam (%21) değil; Güney Kıbrıs'ta çalışma (%59), girişimcilik (%57) ve uzaktan çalışma (%54) öne çıkıyor

Bu çalışma yalnızca bir tespit değil, bir yol haritasıdır. Elde ettiğimiz veriler ışığında; diaspora bilgi platformunun oluşturulması, enerji ve internet altyapısının güçlendirilmesi, girişimcilik desteklerinin hayata geçirilmesi ve liyakat temelli istihdam reformu başta olmak üzere somut bulgu ve önerilerimizi paylaşmaktayız.

Katılımcılarımızdan biri bu durumu en güzel şekilde özetliyor:
*"Lütfen gençlere 'gidin de dönmeyin, bu ülkeden bir şey olmaz' demek yerine yaşayabilecekleri bir ülke yaratın."*

KTGPB olarak, bu verilerin karar alıcılar tarafından dikkate alınması, genç profesyonellerin sesinin duyulması ve somut adımların atılması için çalışmalarımızı kararlılıkla sürdüreceğiz.

Tüm halkımızı bu raporu okuyup gençlerimizin sesini duymaya davet ediyor, ilgili kurum ve karar alıcılarla çözüm odaklı iş birliğine her zaman hazır olduğumuzu vurguluyoruz.

Raporun tamamına **Raporlar** sayfasından ulaşabilirsiniz.`,
    contentEn: `As the Turkish Cypriot Young Professionals Union (KTGPB), we are pleased to share the results of our "Return to Cyprus: Barriers and Needs" survey, conducted between January and February 2026 with the participation of 145 young professionals living abroad.

In this comprehensive study of approximately 30 questions, we evaluated young professionals' experiences, concerns and expectations directly from their own perspectives — covering topics such as career barriers, living conditions, infrastructure needs, access to information and preferred working models.

**📌 Key findings:**
▪️ 42% of participants are conditional or undecided about returning. With the right policies, this group can be won back
▪️ Merit-based career progression and salary satisfaction lead the list of career factors
▪️ 81% of participants find it difficult or very difficult to find a qualified position in their field
▪️ Electricity continuity (4.64/5) and internet stability (4.45/5) are the two highest-rated concerns of the entire survey
▪️ Access to information about incentives and support scores only 1.66 out of 5. 86% of participants get their information from family and friends
▪️ The preferred working model is not local employment (21%); working in South Cyprus (59%), entrepreneurship (57%) and remote work (54%) stand out

This study is not just an assessment — it is a roadmap. In light of the data we have collected, we share our concrete findings and proposals, including the establishment of a diaspora information platform, strengthening of energy and internet infrastructure, the introduction of entrepreneurship support, and merit-based employment reform.

One of our participants summarizes the situation perfectly:
*"Instead of telling young people 'go and don't come back, nothing will come of this country', please create a country they can live in."*

As KTGPB, we will continue our work with determination so that this data is taken into account by decision-makers, the voices of young professionals are heard, and concrete steps are taken.

We invite all our people to read this report and hear the voice of our youth, and we emphasize that we are always ready for solution-oriented cooperation with relevant institutions and decision-makers.

You can access the full report on the **Reports** page.`,
    category: 'general',
    important: true
  },
  {
    id: 2,
    date: '2025-02-21',
    titleTr: 'Yeni Web Sitemiz Yayında!',
    titleEn: 'Our New Website is Live!',
    summaryTr: 'Yenilenen yüzümüzle karşınızdayız. Modern tasarım, iş ilanları, interaktif harita ve çok daha fazlası.',
    summaryEn: 'We are here with our renewed face. Modern design, job listings, interactive map and much more.',
    contentTr: `Değerli Üyelerimiz ve Ziyaretçilerimiz,

Uzun süredir üzerinde çalıştığımız yeni web sitemizi sizlerle paylaşmaktan büyük mutluluk duyuyoruz!

**Yeni Özellikler:**
*   **Modern Arayüz:** Kullanıcı deneyimini ön planda tutan, animasyonlarla zenginleştirilmiş temiz tasarım.
*   **Çoklu Dil Desteği:** Türkçe ve İngilizce tam içerik desteği.
*   **İnteraktif Üye Haritası:** Dünya genelindeki üyelerimizi interaktif harita üzerinde keşfedin, ülkelere göre dağılımı görün.
*   **İş İlanları Sayfası:** Topluluğumuz tarafından paylaşılan kariyer fırsatlarını ülke, şehir ve anahtar kelimeye göre filtreleyin. Siz de ilan paylaşmak isterseniz sayfadaki formu kullanabilirsiniz.
*   **Etkinlik Galerisi:** Geçmiş ve yaklaşan etkinliklerimizi fotoğraf galerisiyle birlikte takip edin.
*   **Ekip Tanıtım Kartları:** Yönetim, disiplin ve denetleme kurulu üyelerimizi interaktif kartlarla tanıyın. Kartlara tıklayarak detaylı bilgilere ulaşabilirsiniz.
*   **LinkedIn Bağlantıları:** Tüm ekip üyelerimizin LinkedIn profillerine doğrudan erişim.

Geri bildirimleriniz bizim için çok değerlidir. Herhangi bir sorunuz veya öneriniz varsa lütfen iletişim bölümünden bize ulaşın.

KTGPB Ekibi`,
    contentEn: `Dear Members and Visitors,

We are delighted to share our new website that we have been working on for a long time!

**New Features:**
*   **Modern Interface:** Clean design enriched with animations, prioritizing user experience.
*   **Multi-language Support:** Full content available in both Turkish and English.
*   **Interactive Member Map:** Explore our members worldwide on an interactive map and see the distribution by country.
*   **Job Listings Page:** Filter career opportunities shared by our community by country, city, and keyword. You can also submit your own listing through the form on the page.
*   **Event Gallery:** Follow our past and upcoming events with photo galleries.
*   **Interactive Team Cards:** Get to know our board, disciplinary, and supervisory committee members through interactive cards. Click on them to reveal detailed information.
*   **LinkedIn Integration:** Direct access to all team members' LinkedIn profiles.

Your feedback is very valuable to us. If you have any questions or suggestions, please reach out through the contact section.

KTGPB Team`,
    category: 'general',
    important: true
  }
];

const Duyurular = () => {
  const { language, t } = useLanguage();
  const [selectedId, setSelectedId] = useState(announcements[0].id);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 900);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredAnnouncements = announcements.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      item.titleTr.toLowerCase().includes(searchLower) ||
      item.titleEn.toLowerCase().includes(searchLower) ||
      item.summaryTr.toLowerCase().includes(searchLower) ||
      item.summaryEn.toLowerCase().includes(searchLower);
    
    return matchesSearch;
  });

  const selectedAnnouncement = announcements.find(a => a.id === selectedId);

  const handleSelect = (id) => {
    setSelectedId(id);
    if (isMobileView) {
      setMobileShowDetail(true);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <PageTransition>
    <section id="duyurular" className="news-hub-section">
      <div className="news-hub-container">
        
        {/* LEFT PANEL: LIST & FILTERS */}
        <div className={`news-sidebar ${isMobileView && mobileShowDetail ? 'hidden' : ''}`}>
          <div className="sidebar-header">
            <h2>{t('announcements.title')}</h2>
            <div className="search-box">
              <i className="fa fa-search" aria-hidden="true"></i>
              <input 
                type="text" 
                placeholder={language === 'tr' ? 'Duyurularda ara...' : 'Search announcements...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="news-list">
            {filteredAnnouncements.length > 0 ? (
              filteredAnnouncements.map(item => (
                <motion.div
                  key={item.id}
                  className={`news-item ${selectedId === item.id ? 'selected' : ''}`}
                  onClick={() => handleSelect(item.id)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="item-meta">
                    <span className="item-date">{formatDate(item.date)}</span>
                    {item.important && <i className="fa fa-star important-icon" aria-hidden="true"></i>}
                  </div>
                  <h3 className="item-title">
                    {language === 'tr' ? item.titleTr : item.titleEn}
                  </h3>
                  <p className="item-summary">
                    {language === 'tr' ? item.summaryTr : item.summaryEn}
                  </p>
                </motion.div>
              ))
            ) : (
              <div className="no-results">
                <i className="fa fa-inbox" aria-hidden="true"></i>
                <p>{language === 'tr' ? 'Sonuç bulunamadı' : 'No results found'}</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: CONTENT DETAIL */}
        <AnimatePresence mode="wait">
          {(!isMobileView || mobileShowDetail) && selectedAnnouncement && (
            <motion.div 
              className="news-content-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              key={selectedAnnouncement.id}
            >
              {isMobileView && (
                <button className="back-button" onClick={() => setMobileShowDetail(false)}>
                  <i className="fa fa-arrow-left" aria-hidden="true"></i> {language === 'tr' ? 'Geri' : 'Back'}
                </button>
              )}

              <div className="content-header">
                <div className="header-badges">
                  <span className="date-badge">
                    <i className="fa fa-clock-o" aria-hidden="true"></i>
                    {formatDate(selectedAnnouncement.date)}
                  </span>
                </div>
                <h1>{language === 'tr' ? selectedAnnouncement.titleTr : selectedAnnouncement.titleEn}</h1>
              </div>

              <div className="content-body">
                {(language === 'tr' ? selectedAnnouncement.contentTr : selectedAnnouncement.contentEn)
                  .split('\n').map((line, i) => {
                    // Simple markdown-like parsing for bold text
                    const parts = line.split(/(\*\*.*?\*\*)/g);
                    return (
                      <p key={i}>
                        {parts.map((part, j) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={j}>{part.slice(2, -2)}</strong>;
                          }
                          return part;
                        })}
                      </p>
                    );
                  })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
    </PageTransition>
  );
};

export default Duyurular;
